import { convertToImperialDate } from "app/codes";
import React, { useEffect, useMemo, useState } from "react";
import SectionSetting from "components/common/SectionSetting";
import { getStatusStyle, isHseAlarm } from "service/events";
import {
  getAssetEventTimeline,
  getAssetAlarms,
  getAssetWellEvents,
  getAssetPlannings,
} from "service/events";

// ---------------------------------------------------------------------------
// AV-D6 — Unified event timeline (NEW view; does NOT replace the Alarm /
// EventLog / Planning tables).
//
// The three tables are really one "event" entity seen at three points in time:
//   • EventLog  -> PAST    (已发生)        — completed operations
//   • Alarm     -> PRESENT (进行中)        — UNRESOLVED alarms (acknowledged or
//                  not). Resolution drives temporal, NOT ack: an ack'd-but-still-
//                  active alarm stays PRESENT; only RESOLVED alarms move to PAST.
//                  (`ack` is purely a "已阅 / seen" marker shown as a small badge,
//                   decoupled from the past/present segmentation — see TimelineRow.)
//   • Planning  -> FUTURE  (计划)          — scheduled operations
// Consumes the BE aggregation endpoint
//   GET /api/assets/<asset_id>/events/timeline  ->  { timeline, gantt }
// and renders the `timeline` array as a single chronological stream, segmented
// by the `temporal` (past/present/future) classification the BE returns. If
// that endpoint isn't deployed yet it degrades gracefully (§3): it falls back
// to merging the three existing per-table endpoints client-side so the view is
// still useful, and shows an empty-state if nothing resolves.
//
// BE timeline item shape:
//   { temporal, type:'event_log'|'alarm'|'planning', id, event_type,
//     date, well_name, asset_name, status, severity, detail:{...} }
// ---------------------------------------------------------------------------

// Visual identity per source `type`. Drives the icon + accent colour. Keyed by
// the BE `type` values, with aliases for the client-side fallback merge.
const SOURCE_META = {
  alarm: { icon: "bi-exclamation-octagon-fill", color: "#FF8C00", label: "Alarm" },
  event_log: { icon: "bi-check2-circle", color: "#2E9E5B", label: "Event" },
  event: { icon: "bi-check2-circle", color: "#2E9E5B", label: "Event" },
  planning: { icon: "bi-calendar-event", color: "#4169E1", label: "Planning" },
};

const TEMPORAL_META = {
  past: { label: "Past", sub: "已发生", color: "#8a94a6", dot: "#8a94a6" },
  present: { label: "Present", sub: "进行中", color: "#FF8C00", dot: "#FF8C00" },
  future: { label: "Future", sub: "计划", color: "#4169E1", dot: "#4169E1" },
};

// Order along the axis: past -> present -> future.
const TEMPORAL_ORDER = { past: 0, present: 1, future: 2 };

function sourceMeta(source) {
  return SOURCE_META[String(source || "").toLowerCase()] || {
    icon: "bi-dot",
    color: "#8a94a6",
    label: source || "Event",
  };
}

function toMillis(value) {
  if (value == null || value === "") return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

// Best-effort temporal classification used only when the row lacks an explicit
// `temporal` (i.e. the client-side fallback merge — the BE always stamps one,
// so the "past/present/future归类口径" stays authoritative for PE review). The
// source `type` is the primary signal (matches the three-table semantics); a
// date-vs-now check is the last-resort tie-breaker.
function classifyTemporal(item, source) {
  const explicit = String(item.temporal || "").toLowerCase();
  if (explicit === "past" || explicit === "present" || explicit === "future") {
    return explicit;
  }
  const s = String(source || "").toLowerCase();
  if (s === "alarm") return "present";
  if (s === "event" || s === "event_log") return "past";
  if (s === "planning") return "future";
  const start = toMillis(item.date || item.start_date);
  if (start == null) return "present";
  return start > Date.now() ? "future" : "past";
}

// Normalise one row into the shape the timeline renders. Handles both the BE
// unified item (type/date/detail) and a raw per-table row (the fallback merge).
function normalize(item, sourceHint) {
  const source = (item.type || sourceHint || "").toLowerCase();
  // BE unified item nests the full source row under `detail`; fallback rows are
  // flat. Look in both so tag/parameter/lead etc. resolve either way.
  const detail = item.detail || item;
  const start = item.date || detail.start_date || detail.date || null;
  const end = detail.end_date || null;
  return {
    id: item.id != null ? item.id : detail.id,
    source,
    temporal: classifyTemporal(item, source),
    type: item.event_type || detail.event_type || detail.event || sourceMeta(source).label,
    title:
      detail.event ||
      item.event_type ||
      detail.event_type ||
      detail.tag ||
      detail.parameter ||
      sourceMeta(source).label,
    well_name: item.well_name || detail.well_name,
    start_date: start,
    end_date: end,
    start_ms: toMillis(start),
    severity: item.severity != null ? item.severity : detail.severity,
    status: item.status != null ? item.status : detail.status,
    reporter: detail.reporter || detail.lead,
    hse: source === "alarm" ? isHseAlarm(detail) : false,
    // Ack is a pure "已阅 / seen" marker. It is NOT used for temporal
    // segmentation — the BE now keys past/present on RESOLUTION, so an ack'd
    // alarm can still be PRESENT. Surfaced only as a small badge.
    acked:
      source === "alarm" &&
      !!(detail.ack_time || String(item.status || detail.status || "").toLowerCase() === "acknowledged"),
    _raw: item,
  };
}

function EventTimeline(props) {
  const { assetId, loadingIconActions } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];
  const [items, setItems] = useState(null); // null = not loaded, [] = empty
  const [degraded, setDegraded] = useState(false); // fell back to per-table merge
  const [filter, setFilter] = useState("ALL"); // ALL | past | present | future

  useEffect(() => {
    if (assetId == null) {
      setItems(null);
      return;
    }
    let cancelled = false;
    setItems(null);
    setDegraded(false);
    showLoadingIcon && showLoadingIcon();

    const finish = (rows, isDegraded) => {
      if (cancelled) return;
      setItems(rows);
      setDegraded(!!isDegraded);
      hideLoadingIcon && hideLoadingIcon();
    };

    // Primary: the unified BE aggregation endpoint. Response is
    // { timeline:[...], gantt:[...] }; we render the timeline array here.
    getAssetEventTimeline(
      assetId,
      (data) => {
        const arr = Array.isArray(data)
          ? data
          : data && Array.isArray(data.timeline)
          ? data.timeline
          : [];
        finish(arr.map((r) => normalize(r)), false);
      },
      () => {
        // §3 graceful degradation: endpoint not ready -> merge the three
        // existing per-table endpoints client-side so the view still works.
        if (cancelled) return;
        mergeFallback(assetId, (rows) => finish(rows, true));
      }
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  // Fallback aggregation: pull alarms (present) + events (past) + plannings
  // (future) and tag each with its source so classifyTemporal can place it.
  function mergeFallback(id, done) {
    const collected = [];
    let pending = 3;
    const settle = () => {
      pending -= 1;
      if (pending === 0) done(collected);
    };
    const collect = (sourceHint) => (data) => {
      if (Array.isArray(data)) {
        data.forEach((r) => collected.push(normalize(r, sourceHint)));
      }
      settle();
    };
    getAssetAlarms(id, collect("alarm"), settle);
    getAssetWellEvents(id, collect("event_log"), settle);
    getAssetPlannings(id, collect("planning"), settle);
  }

  // Sort chronologically along the past->present->future axis. Within the same
  // temporal band, order by date (ascending so the eye reads left/top = older).
  const sorted = useMemo(() => {
    if (!items) return null;
    const filtered =
      filter === "ALL" ? items : items.filter((i) => i.temporal === filter);
    return filtered.slice().sort((a, b) => {
      const ta = TEMPORAL_ORDER[a.temporal] ?? 1;
      const tb = TEMPORAL_ORDER[b.temporal] ?? 1;
      if (ta !== tb) return ta - tb;
      const da = a.start_ms ?? 0;
      const db = b.start_ms ?? 0;
      return da - db;
    });
  }, [items, filter]);

  const counts = useMemo(() => {
    const c = { past: 0, present: 0, future: 0 };
    (items || []).forEach((i) => {
      if (c[i.temporal] != null) c[i.temporal] += 1;
    });
    return c;
  }, [items]);

  return (
    <div className="cards-container h-100">
      <div className="cards-header d-flex flex-row justify-content-between align-items-center">
        <span className="header-title">
          Event Timeline
          {degraded && (
            <span
              className="badge ms-2"
              style={{ background: "rgba(255,184,0,0.15)", color: "#FFB800" }}
              title="Unified endpoint unavailable — merged from the three event sources client-side."
            >
              merged
            </span>
          )}
        </span>
        <div className="header-menu-container d-flex flex-row align-items-center">
          <div className="btn-group btn-group-sm me-2" role="group">
            {["ALL", "past", "present", "future"].map((key) => {
              const meta = TEMPORAL_META[key];
              const active = filter === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`btn btn-sm ${
                    active ? "btn-secondary" : "btn-outline-secondary"
                  }`}
                  onClick={() => setFilter(key)}
                >
                  {key === "ALL"
                    ? "All"
                    : `${meta.label} (${counts[key]})`}
                </button>
              );
            })}
          </div>
          <div className="cards-setting-btn">
            <SectionSetting disable />
          </div>
        </div>
      </div>

      <div className="cards-content">
        <div className="overflow-auto hide-scrollbar" style={{ maxHeight: 360 }}>
          <EventTimelineBody rows={sorted} />
        </div>
      </div>
    </div>
  );
}

// The vertical past->present->future stream. Each temporal band is labelled
// once, then its events render as a rail of cards.
function EventTimelineBody({ rows }) {
  if (rows == null) {
    return (
      <div className="text-muted text-center p-4">Loading timeline…</div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="text-muted text-center p-4">
        <i className="bi bi-clock-history d-block mb-2" style={{ fontSize: 24 }} />
        No events on the timeline for this asset.
      </div>
    );
  }

  // Group consecutive rows by temporal so we can stamp a band header.
  const bands = [];
  let current = null;
  rows.forEach((row) => {
    if (!current || current.temporal !== row.temporal) {
      current = { temporal: row.temporal, rows: [row] };
      bands.push(current);
    } else {
      current.rows.push(row);
    }
  });

  return (
    <div style={{ paddingLeft: 8 }}>
      {bands.map((band, bi) => {
        const tm = TEMPORAL_META[band.temporal] || TEMPORAL_META.present;
        return (
          <div key={`${band.temporal}-${bi}`} className="mb-2">
            <div
              className="d-flex flex-row align-items-center mb-1"
              style={{ position: "sticky", top: 0 }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: tm.dot,
                  marginRight: 8,
                }}
              />
              <span className="fw-semibold" style={{ color: tm.color }}>
                {tm.label}
              </span>
              <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                {tm.sub}
              </span>
            </div>
            <div
              style={{
                borderLeft: `2px solid ${tm.dot}`,
                marginLeft: 4,
                paddingLeft: 12,
              }}
            >
              {band.rows.map((row, ri) => (
                <TimelineRow key={row.id ?? `${bi}-${ri}`} row={row} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TimelineRow({ row }) {
  const sm = sourceMeta(row.source);
  const sev = row.severity ? getStatusStyle(row.severity) : null;
  const accent = row.hse ? "#FF5454" : sm.color;
  return (
    <div
      className="d-flex flex-row align-items-start mb-2 p-2"
      style={{
        background: row.hse ? "rgba(255,84,84,0.08)" : "rgba(255,255,255,0.03)",
        borderLeft: `3px solid ${accent}`,
        borderRadius: 4,
      }}
    >
      <i
        className={`bi ${row.hse ? "bi-exclamation-triangle-fill" : sm.icon} me-2`}
        style={{ color: accent, fontSize: 16, lineHeight: "1.4" }}
        title={sm.label}
      />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex flex-row justify-content-between align-items-center">
          <span className="fw-semibold text-truncate" style={{ color: "#fff" }}>
            {row.hse && (
              <span
                className="badge me-1"
                style={{ background: "#FF5454", color: "#fff" }}
              >
                HSE
              </span>
            )}
            {row.title}
          </span>
          <span className="text-muted ms-2" style={{ whiteSpace: "nowrap", fontSize: 12 }}>
            {row.start_date ? convertToImperialDate(row.start_date) : "—"}
          </span>
        </div>
        <div className="d-flex flex-row align-items-center mt-1" style={{ fontSize: 12 }}>
          <span
            className="badge me-2"
            style={{ background: "rgba(255,255,255,0.08)", color: sm.color }}
          >
            {sm.label}
          </span>
          {row.well_name && (
            <span className="text-muted me-2 text-truncate">{row.well_name}</span>
          )}
          {row.acked && (
            <span
              className="badge me-2"
              style={{ background: "rgba(138,148,166,0.18)", color: "#8a94a6" }}
              title="Acknowledged — 已阅（不影响过去/现在归段）"
            >
              已阅
            </span>
          )}
          {row.status && <span className="text-muted me-2">{row.status}</span>}
          {sev && (
            <span
              className="badge"
              style={{ background: sev.bg, color: sev.textColor }}
            >
              {row.severity}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export { EventTimeline };
export default EventTimeline;
